import { PracticeTherapyDepartment } from "@physiofit-digital/physiofit_types";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { CardButton } from "../../core/components/buttons/CardButton";
import { InfoText } from "../../core/components/texts/InfoText";
import { getChainConfigLoaded } from "../../core/store/chainSlice";
import { useMainStore } from "../../core/store/mainStore";
import { useHandleParamNavigation } from "../../core/utils/useHandleParamNavigation";
import { AppointmentScreenWrapper } from "../onboarding/AppointmentScreenWrapper";
import { useTherapyDepartmentSkip } from "./utils/appointmentFlow";

const DEPARTMENT_LABEL_KEYS: Record<PracticeTherapyDepartment, string> = {
  physiotherapy: "THERAPY_DEPARTMENT_PHYSIOTHERAPY",
  occupationalTherapy: "THERAPY_DEPARTMENT_OCCUPATIONAL_THERAPY",
  osteopathy: "THERAPY_DEPARTMENT_OSTEOPATHY",
  speechTherapy: "THERAPY_DEPARTMENT_SPEECH_THERAPY",
  podology: "THERAPY_DEPARTMENT_PODOLOGY",
  nutritionTherapy: "THERAPY_DEPARTMENT_NUTRITION_THERAPY",
  trainingTherapy: "THERAPY_DEPARTMENT_TRAINING_THERAPY",
};

export const AppointmentDepartmentSelectionScreen = () => {
  const { t } = useTranslation();
  const navigate = useHandleParamNavigation();
  const chainConfigLoaded = useMainStore(getChainConfigLoaded);
  const setDepartment = useMainStore(
    (state) => state.setAppointmentTherapyDepartment,
  );
  const { isSkipped, departments } = useTherapyDepartmentSkip();

  // Safety net: the screen stays reachable by sidebar jump and must not show up as a selection then.
  useEffect(() => {
    if (!chainConfigLoaded || !isSkipped) return;
    setDepartment(undefined);
    navigate("/appointment/type", { replace: true });
  }, [chainConfigLoaded, isSkipped, navigate, setDepartment]);

  if (!chainConfigLoaded || isSkipped) return null;

  const handleSelect = (department: PracticeTherapyDepartment) => {
    setDepartment(department);
    navigate("/appointment/type");
  };

  return (
    <AppointmentScreenWrapper hideNextButton>
      <InfoText
        headline={t("APPOINTMENT_DEPARTMENT_SELECTION_HEADLINE")}
        text={[t("APPOINTMENT_DEPARTMENT_SELECTION_DESCRIPTION")]}
      />
      <CardsContainer>
        {departments.map((department) => (
          <CardButton
            key={department}
            onClick={() => handleSelect(department)}
            title={t(DEPARTMENT_LABEL_KEYS[department])}
          />
        ))}
      </CardsContainer>
    </AppointmentScreenWrapper>
  );
};

const CardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;
